import type { SelfReport, SelfReportPatch, SelfReportProvenance } from './api'

/**
 * The journal: its vocabulary, and the diff that is the only thing ever sent.
 *
 * ── WHY THIS IS A DIFF AND NOT A SERIALISED FORM ────────────────────────────
 *
 * A journal is hand-entered, time-bound and unreconstructable, so losing one is
 * permanent — the backend's own `SelfReport` docstring says so, and it is why
 * `SelfReportPatch` exists as a separate model from `SelfReport`. On the patch
 * route an **omitted** field is preserved and an **explicit `null`** clears it
 * (§3.3). The obvious client — read the form, send every field — therefore
 * clears everything the user left blank, and `SelfReport.raw` with it. It would
 * report success while deleting the verbatim journal the acquisition app wrote.
 *
 * So: `buildPatch` emits a key only for a field the user actually acted on AND
 * whose value differs from what the server last told us. Everything else is
 * absent, which is the wire's own word for "leave it alone".
 *
 * ── WHY `touched` IS NOT REDUNDANT WITH THE COMPARISON ──────────────────────
 *
 * Two cases break a pure value diff, in opposite directions:
 *
 *   - A record holding `{state: null, state_quadrant: 'HVLA'}` pre-fills the
 *     felt-state control as "Calm" through `QUADRANT_STATES` (otherwise the
 *     screen would read "unanswered" about a record that holds an answer). On a
 *     pure value diff that pristine form is already DIRTY: Save goes live with
 *     zero user action, and one press writes a felt state nobody chose and flips
 *     `self_report_provenance.edited` to true.
 *   - Take the displayed value as the baseline instead, and the opposite breaks:
 *     a user who presses "Calm" to affirm that same record produces nothing.
 *
 * `touched` plus a comparison against the STORED value answers both. It also
 * makes a tag tapped on and straight back off a no-op rather than an affirmative
 * "none of these applied" manufactured out of an undo.
 *
 * ── AND IT IS WHAT MAKES THE FORM SAFE TO REUSE ─────────────────────────────
 *
 * Sprint 1.9 Stage 6 mounts the same component in the History detail view. On a
 * pure value diff, ANY host that renders a subset of the fields clears every
 * field it did not render — the whole journal wiped by ordinary reuse rather
 * than by a mistake. A field that was never rendered cannot be touched, so it
 * cannot be sent. That property is the reason the rule is shaped this way.
 *
 * ── THE VOCABULARY IS NOT OURS ──────────────────────────────────────────────
 *
 * `calibrations/` — the acquisition app — owns this document. The labels and
 * hints below are copied from `calibrations/static/main.js:54-70` and its
 * journal form, and `STATE_PAIRS` mirrors `calibrations/backend/app.py:105`.
 * Reproducing a documented journal-enum mapping is explicitly permitted by
 * acceptance criterion 2; INVENTING one is not. A person who answers
 * "Calm (content, low energy)" on the device and is offered "Relaxed" here has
 * been asked a different question about the same day.
 */

export type JournalState = 'energized' | 'calm' | 'tense' | 'flat' | 'neutral'
export type JournalQuadrant = 'HVHA' | 'HVLA' | 'LVHA' | 'LVLA' | 'neutral'
export type JournalQuality = 'poor' | 'ok' | 'great'
export type JournalIntensity = 'light' | 'moderate' | 'hard'
export type JournalTag = NonNullable<SelfReport['tags']>[number]
/** Schema 2. How long before THIS recording something happened. */
export type JournalWhenAgo = 'lt_1h' | '1_to_3h' | 'gt_3h'
/** The same three, plus the explicit `none` that zero cups has as an ANSWER. */
export type JournalCaffeineLast = 'none' | JournalWhenAgo

/**
 * `state` → `state_quadrant`, mirroring `JOURNAL_STATE_QUADRANT` in
 * `calibrations/backend/app.py:105`.
 *
 * **The client is the only guard here.** The backend accepts a contradictory
 * pair — `SelfReport(state='energized', state_quadrant='LVLA')` validates and is
 * stored — so nothing downstream will complain. The felt state is collected
 * ONCE and the quadrant derived, which makes a contradiction unrepresentable
 * rather than merely validated against.
 */
export const STATE_PAIRS: Readonly<Record<JournalState, JournalQuadrant>> = {
  energized: 'HVHA',
  calm: 'HVLA',
  tense: 'LVHA',
  flat: 'LVLA',
  neutral: 'neutral',
}

/**
 * The exact inverse, for DISPLAY only.
 *
 * A captured journal can hold a quadrant with no `state`: `from_journal` drops
 * one unparseable cell rather than failing the whole report
 * (`contracts/recording.py:326`), so a bad `state` with a good `state_quadrant`
 * is a real degradation path. Without this the control reads "unanswered" about
 * a record that holds an answer. A value recovered through this map is never on
 * its own a reason to write anything — see `touched`.
 */
export const QUADRANT_STATES: Readonly<Record<JournalQuadrant, JournalState>> = {
  HVHA: 'energized',
  HVLA: 'calm',
  LVHA: 'tense',
  LVLA: 'flat',
  neutral: 'neutral',
}

/** Label and visible hint, from `calibrations/static/main.js:54-60`. */
export const STATE_OPTIONS: ReadonlyArray<{
  value: JournalState
  label: string
  hint: string
}> = [
  { value: 'energized', label: 'Energized', hint: 'alert, high energy' },
  { value: 'calm', label: 'Calm', hint: 'content, low energy' },
  { value: 'tense', label: 'Tense', hint: 'stressed, high energy' },
  { value: 'flat', label: 'Flat', hint: 'low, depleted' },
  { value: 'neutral', label: 'Neutral', hint: 'nothing much' },
]

/**
 * The eight, in the acquisition app's order (`main.js:61-70`).
 *
 * Emitted in this order so a stored value is deterministic rather than dependent
 * on which chip was pressed first.
 */
export const JOURNAL_TAGS = [
  'screens_before_bed',
  'time_outdoors',
  'meditated',
  'high_load_day',
  'socialized',
  'ate_late',
  'caffeine_after_2pm',
  'felt_anxious',
] as const satisfies readonly JournalTag[]

/**
 * Exhaustive on purpose, and deliberately NOT the open
 * `Record<string, X | undefined>` shape `RECORDING_OUTCOME` uses. That map is
 * loose because four recording statuses are reserved values with no writer; this
 * vocabulary is closed, so a contract change that adds a tag should be a
 * COMPILE ERROR here rather than a silently unlabelled chip.
 */
export const TAG_LABELS: Readonly<Record<JournalTag, string>> = {
  screens_before_bed: 'Screens before bed',
  time_outdoors: 'Time outdoors',
  meditated: 'Meditated',
  high_load_day: 'High-load day',
  socialized: 'Socialized',
  ate_late: 'Ate late',
  caffeine_after_2pm: 'Caffeine after 2pm',
  felt_anxious: 'Felt anxious',
}

/**
 * The eight tags, shown in two groups (Sprint 1.9.11, concept §A).
 *
 * ── TWO GROUPS ON SCREEN, ONE LIST ON THE WIRE ──────────────────────────────
 *
 * Schema 2 anchors every question to the recording, and the tags are the last
 * place that was left undone: "Screens before bed" is about last night and
 * "Time outdoors" is about this morning, and a single undifferentiated row said
 * neither. This splits the ASKING, not the storing — `tags` keeps one key, one
 * type and one canonical order (`JOURNAL_TAGS`), so no stored journal changes
 * shape and nothing has to migrate.
 *
 * Exhaustive by construction: the flattened members are checked against
 * `JOURNAL_TAGS` below, so a ninth tag added to the vocabulary and not placed in
 * a group is a compile error rather than a chip that quietly stops being shown.
 */
export const TAG_GROUPS = [
  { label: 'Last night', tags: ['screens_before_bed', 'ate_late', 'caffeine_after_2pm'] },
  {
    label: 'Today so far',
    tags: ['time_outdoors', 'meditated', 'high_load_day', 'socialized', 'felt_anxious'],
  },
] as const satisfies readonly { label: string; tags: readonly JournalTag[] }[]

/**
 * Every tag is in exactly one group.
 *
 * A type-level assertion, so it costs nothing at runtime and cannot be skipped:
 * if the union of the groups' members ever stops being the whole of
 * `JournalTag`, or starts including something that is not one, this stops
 * compiling. A test could be deleted; this cannot be, without the file failing
 * to build.
 */
type _GroupedTags = (typeof TAG_GROUPS)[number]['tags'][number]
type _AssertAllTagsGrouped = [JournalTag] extends [_GroupedTags]
  ? [_GroupedTags] extends [JournalTag]
    ? true
    : never
  : never
const _TAGS_ARE_FULLY_GROUPED: _AssertAllTagsGrouped = true
void _TAGS_ARE_FULLY_GROUPED

/** `main.js:1103-1106`. Note "OK", not "Ok". */
export const QUALITY_LABELS: Readonly<Record<JournalQuality, string>> = {
  poor: 'Poor',
  ok: 'OK',
  great: 'Great',
}

/** `main.js:1127-1130`. */
export const INTENSITY_LABELS: Readonly<Record<JournalIntensity, string>> = {
  light: 'Light',
  moderate: 'Moderate',
  hard: 'Hard',
}

/**
 * Schema 2's two chip rows, in the order the concept draws them.
 *
 * Exhaustive `Record`s for the reason `TAG_LABELS` is: these vocabularies are
 * closed, so a contract change that adds a member should be a COMPILE ERROR
 * here rather than a silently unlabelled chip.
 */
export const WHEN_AGO_LABELS: Readonly<Record<JournalWhenAgo, string>> = {
  lt_1h: 'Under 1 h ago',
  '1_to_3h': '1–3 h ago',
  gt_3h: 'Over 3 h ago',
}

/** `none` reads as "none yet" when asked and as "no caffeine yet" when shown. */
export const CAFFEINE_LAST_LABELS: Readonly<Record<JournalCaffeineLast, string>> = {
  none: 'None yet',
  lt_1h: 'Under 1 h ago',
  '1_to_3h': '1–3 h ago',
  gt_3h: 'Over 3 h ago',
}

/**
 * What a freshly written journal is stamped with.
 *
 * Schema 2 (Sprint 1.9.11) anchored every question to the recording it sits
 * beside: three new keys, two reworded questions, and **nothing removed,
 * renamed or retyped**. That last clause is what lets one form render both and
 * one contract read both — and it is also why `shouldStampSchemaVersion` below
 * may RAISE a stored 1 to 2 and must never lower it.
 */
export const JOURNAL_SCHEMA_VERSION = 2

/* ── The numeric fields ────────────────────────────────────────────────────── */

export interface NumericBounds {
  min: number
  /** Absent where the contract has none — see `caffeine_cups`. */
  max?: number
  /** Increment for the stepper BUTTONS only. Typed input is never constrained by it. */
  step: number
  unit: string
  label: string
}

/**
 * Bounds copied from the contract, and steps from the acquisition app's own form.
 *
 * **`max` is absent for the three intake fields on purpose.** `StrictJournalFloat`
 * is `ge=0` with no upper bound, so inventing a ceiling here would refuse an
 * answer the server accepts. The one deviation from `main.js` is
 * `exercise_minutes`, which the device steps by 1: tapping to 65 one minute at a
 * time is not an affordance, and typing is unconstrained either way.
 */
export const NUMERIC_FIELDS = {
  sleep_hours: { min: 0, max: 24, step: 0.25, unit: 'hours', label: 'Sleep hours' },
  exercise_minutes: { min: 0, max: 1440, step: 5, unit: 'minutes', label: 'Minutes' },
  caffeine_cups: { min: 0, step: 0.5, unit: 'cups', label: 'Caffeine cups' },
  alcohol_drinks: { min: 0, step: 0.5, unit: 'drinks', label: 'Alcohol drinks' },
  water_glasses: { min: 0, step: 1, unit: 'glasses', label: 'Water glasses' },
} as const satisfies Record<string, NumericBounds>

export type NumericField = keyof typeof NUMERIC_FIELDS

/**
 * Whether a stored journal field holds an answer at all.
 *
 * `0` and `false` ARE answers — "no caffeine" and "did not exercise" are the
 * two a person is most likely to have given deliberately, and a truthiness
 * test drops both. Only `null` (explicitly cleared, or never asked) and
 * `undefined` (a key the record does not carry) are absences, and they are the
 * same absence to a reader.
 *
 * Lives here, beside the vocabulary, because both read-backs need it: it was
 * `JournalSummary`'s own one-liner until the Journal view's cells wanted the
 * same rule (Sprint 1.9.11 J2b). A second copy is how `0 cups` comes to be
 * shown on one screen and dropped on the other.
 *
 * NOT the private `isAnswered` further down this file, and the difference is
 * deliberate: that one answers a question about a PATCH ("does this field
 * carry an answer to send"), where an empty tag list is the absence of one.
 * This one answers a question about a STORED field being read back, where
 * `tags: []` is a journal that was answered with no tags.
 */
export function hasAnswer(value: unknown): boolean {
  return value !== null && value !== undefined
}

export type NumericRead =
  | { kind: 'empty' }
  | { kind: 'value'; value: number }
  | { kind: 'invalid'; reason: string }

/**
 * Digits, at most one full stop, nothing else.
 *
 * **This runs BEFORE `Number()`, and that order is the point.** `Number()` is a
 * laundering function: it reads `'0x10'` as 16, `'1e3'` as 1000, `'Infinity'` as
 * Infinity and `''` as **0** — and `caffeine_cups: 0` is a real answer, so an
 * emptied field coerced to 0 is a fabricated one. `parseFloat` is no better in
 * the other direction (`''` → NaN, which the contract refuses with
 * `allow_inf_nan=False`). Neither is used bare anywhere in this file.
 *
 * No sign is permitted because every numeric journal field is `ge=0`, which also
 * disposes of a lone `'-'`. A lone `'.'` fails both branches.
 */
const NUMERIC_SHAPE = /^(?:\d+(?:\.\d*)?|\.\d+)$/

/**
 * Read one numeric field's text.
 *
 * `''` is **unanswered**, and is returned as such rather than as 0 — the whole
 * reason the draft holds text instead of a parsed number. A half-typed `'8.'`
 * reads as 8, so the value survives a submit made mid-keystroke.
 */
export function readNumeric(text: string, bounds: NumericBounds): NumericRead {
  // ── A COMMA IS NOT A MISTAKE ON THE TARGET DEVICE ────────────────────────
  // `inputMode="decimal"` offers a comma as the separator in comma-decimal
  // locales, and criterion 14 names the iPhone specifically. Rejecting it would
  // mean a user in such a locale simply cannot enter 7,5. One comma and no full
  // stop is a decimal separator; anything else is left to fail the shape check.
  const commas = text.split(',').length - 1
  const normalised = commas === 1 && !text.includes('.') ? text.replace(',', '.') : text

  const trimmed = normalised.trim()
  if (trimmed === '') return { kind: 'empty' }

  if (!NUMERIC_SHAPE.test(trimmed)) return { kind: 'invalid', reason: 'Use digits, like 7 or 7.5.' }

  const value = Number(trimmed)
  // A literal long enough to overflow parses to Infinity and passes the shape.
  if (!Number.isFinite(value)) return { kind: 'invalid', reason: 'That number is too large.' }

  if (value < bounds.min) return { kind: 'invalid', reason: `Cannot be less than ${bounds.min}.` }
  if (bounds.max !== undefined && value > bounds.max) {
    return { kind: 'invalid', reason: `Cannot be more than ${bounds.max}.` }
  }
  return { kind: 'value', value }
}

/**
 * Render a stored number back into input text.
 *
 * `String(n)` and nothing else. **No `toFixed`, no rounding, no snapping to
 * `step`.** A stored `sleep_hours: 7.53` is legal, and a formatter here would
 * mean a user who merely glances at the field — or presses `+` then `−` — writes
 * 7.5 over a captured measurement. That is the failure class which passes every
 * test built from tidy fixtures and fires only on real data.
 */
export function numericText(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value)
}

/* ── The clock field ───────────────────────────────────────────────────────── */

/**
 * `woke_at_local`: `"HH:MM"` on a 24-hour clock, and nothing else.
 *
 * The same three-state shape `readNumeric` returns, so `draftErrors` picks it up
 * with no new mechanism. The contract's pattern is
 * `^([01][0-9]|2[0-3]):[0-5][0-9]$` and this is the same rule written once more
 * on this side — the field is **refused, never repaired**, for the reason
 * `numericText` is `String(n)`: a value this client would have to rewrite to
 * make legal is one somebody did not mean to type, and silently padding `"7:00"`
 * to `"07:00"` is the client editing an answer.
 *
 * `<input type="time">` already yields `"HH:MM"` in every supporting browser, so
 * in practice this guards a paste, a browser without the control, and the tests.
 */
const CLOCK_SHAPE = /^([01][0-9]|2[0-3]):[0-5][0-9]$/

export type ClockRead =
  | { kind: 'empty' }
  | { kind: 'value'; value: string }
  | { kind: 'invalid'; reason: string }

export function readClockTime(text: string): ClockRead {
  const trimmed = text.trim()
  if (trimmed === '') return { kind: 'empty' }
  if (!CLOCK_SHAPE.test(trimmed)) {
    return { kind: 'invalid', reason: 'Use a 24-hour time, like 06:40.' }
  }
  return { kind: 'value', value: trimmed }
}

/* ── The draft ─────────────────────────────────────────────────────────────── */

/**
 * What the form holds. Twelve keys for fifteen wire fields.
 *
 * `state_quadrant` is absent because it is DERIVED from `state` — the whole
 * mechanism that makes a contradictory pair unrepresentable. `schema_version`
 * and `answered_at` are absent because they are provenance the form never asks a
 * person about.
 *
 * The five numeric fields hold **text, exactly as typed**. That is deliberate and
 * it is the opposite of the obvious choice. A controlled input whose value is
 * re-derived from a parsed number fights the user — `'8.'` and `'.5'` become
 * unenterable mid-keystroke — and holding text means `''` (unanswered) stays
 * distinguishable from `'0'` (an answer) without a special case. It also leaves
 * exactly ONE place where a string becomes a number, which is one place to test
 * and one place a mutation can be caught.
 */
export interface SelfReportDraft {
  state: JournalState | null
  stress_1_10: number | null
  sleep_hours: string
  sleep_quality: JournalQuality | null
  /** Text, like the numerics and for the same reason: `''` is unanswered. */
  woke_at_local: string
  exercised: boolean | null
  exercise_finished: JournalWhenAgo | null
  exercise_intensity: JournalIntensity | null
  exercise_minutes: string
  caffeine_cups: string
  caffeine_last: JournalCaffeineLast | null
  alcohol_drinks: string
  water_glasses: string
  tags: readonly JournalTag[]
  notes: string
}

export type DraftField = keyof SelfReportDraft

const DRAFT_FIELD_LIST = [
  'state',
  'stress_1_10',
  'sleep_hours',
  'sleep_quality',
  'woke_at_local',
  'exercised',
  'exercise_finished',
  'exercise_intensity',
  'exercise_minutes',
  'caffeine_cups',
  'caffeine_last',
  'alcohol_drinks',
  'water_glasses',
  'tags',
  'notes',
] as const

/**
 * `readonly DraftField[]` when the list is COMPLETE, and an object naming the
 * forgotten field when it is not.
 *
 * The annotation this replaces was `readonly DraftField[]`, which says only
 * that every entry is a field — exactly the half that does not matter. The
 * half that does is the other direction: `draftIsEmpty` decides whether a
 * journal gets written AT ALL by iterating this list
 * (`lib/capture/journal.ts`), so a field missing from it is a field that
 * cannot keep a journal alive, and the comment there claiming the list "cannot
 * fall behind the shape" was a belief with no check under it. Adding a key to
 * `SelfReportDraft` and forgetting this list compiled clean and silently
 * dropped answers.
 *
 * It is a conditional type rather than a dangling `type _Assert = ...`
 * because `noUnusedLocals` is on and an unused assertion would have to be
 * exported to survive — and an exported assertion is one `// eslint-disable`
 * away from being deleted as dead. This one is load-bearing by construction:
 * it IS `DRAFT_FIELDS`'s type. The failing branch is a shape no array can
 * satisfy and it carries the missing key's NAME, so `tsc` prints
 * `Property '__missingDraftFields' is missing` beside
 * `__missingDraftFields: "water_glasses"` rather than an anonymous `never`.
 */
type CompleteDraftFields<T extends readonly DraftField[]> =
  [Exclude<DraftField, T[number]>] extends [never]
    ? readonly DraftField[]
    : { __missingDraftFields: Exclude<DraftField, T[number]> }

/** Every field, in the order the form presents them — and provably all of them. */
export const DRAFT_FIELDS: CompleteDraftFields<typeof DRAFT_FIELD_LIST> = DRAFT_FIELD_LIST

export const EMPTY_DRAFT: SelfReportDraft = {
  state: null,
  stress_1_10: null,
  sleep_hours: '',
  sleep_quality: null,
  woke_at_local: '',
  exercised: null,
  exercise_finished: null,
  exercise_intensity: null,
  exercise_minutes: '',
  caffeine_cups: '',
  caffeine_last: null,
  alcohol_drinks: '',
  water_glasses: '',
  tags: [],
  notes: '',
}

/** Canonical order, duplicates collapsed, unknown values dropped. */
const canonicalTags = (tags: readonly string[]): JournalTag[] =>
  JOURNAL_TAGS.filter((tag) => tags.includes(tag))

/**
 * True when the stored report holds a tag this client cannot represent.
 *
 * Unreachable through today's contract — the wire type is a closed union — which
 * is exactly why it needs a written branch rather than a belief. The form
 * disables tag editing in this case: `raw`'s principle generalised, that what the
 * client cannot represent it must not destroy. A disabled control is never
 * touched, so `tags` is then never written.
 */
export function hasUnknownTags(report: SelfReport | null): boolean {
  const stored = report?.tags
  if (!stored) return false
  return stored.some((tag) => !(JOURNAL_TAGS as readonly string[]).includes(tag))
}

/**
 * Seed a draft from the server's report.
 *
 * `state` falls back to the quadrant's state so a half-filled pair displays the
 * answer it holds. That recovered value is for the SCREEN only — `touched` is
 * what keeps it out of the diff.
 */
export function draftFromReport(report: SelfReport | null): SelfReportDraft {
  if (report === null) return { ...EMPTY_DRAFT }
  const quadrant = report.state_quadrant
  return {
    state: report.state ?? (quadrant ? QUADRANT_STATES[quadrant] : null),
    stress_1_10: report.stress_1_10 ?? null,
    sleep_hours: numericText(report.sleep_hours),
    sleep_quality: report.sleep_quality ?? null,
    woke_at_local: report.woke_at_local ?? '',
    exercised: report.exercised ?? null,
    exercise_finished: report.exercise_finished ?? null,
    exercise_intensity: report.exercise_intensity ?? null,
    exercise_minutes: numericText(report.exercise_minutes),
    caffeine_cups: numericText(report.caffeine_cups),
    caffeine_last: report.caffeine_last ?? null,
    alcohol_drinks: numericText(report.alcohol_drinks),
    water_glasses: numericText(report.water_glasses),
    tags: canonicalTags(report.tags ?? []),
    notes: report.notes ?? '',
  }
}

/**
 * Per-field messages for the fields the user has TOUCHED.
 *
 * ── ONLY A TOUCHED FIELD CAN RAISE AN ERROR ─────────────────────────────────
 *
 * A stored `caffeine_cups: 1e-7` is legal (`ge=0`, no upper bound) and renders as
 * `"1e-7"`, which this file's shape check rejects. Validating a pristine field
 * would therefore disable Save over a value the user never looked at, on a field
 * they never opened.
 *
 * ── AND ERRORS ARE A SEPARATE CHANNEL FROM `changedFields` ──────────────────
 *
 * Fold them together and one stray comma makes every other valid answer
 * unsavable while the form reports "nothing changed" to somebody who just
 * answered five questions.
 */
export function draftErrors(
  draft: SelfReportDraft,
  touched: ReadonlySet<DraftField>,
): Partial<Record<DraftField, string>> {
  const errors: Partial<Record<DraftField, string>> = {}
  for (const field of Object.keys(NUMERIC_FIELDS) as NumericField[]) {
    if (!touched.has(field)) continue
    const read = readNumeric(draft[field], NUMERIC_FIELDS[field])
    if (read.kind === 'invalid') errors[field] = read.reason
  }
  // The clock field, under the same touched-only rule and for the same reason:
  // a stored value this build cannot represent must not disable Save on a field
  // the person never opened.
  if (touched.has('woke_at_local')) {
    const read = readClockTime(draft.woke_at_local)
    if (read.kind === 'invalid') errors.woke_at_local = read.reason
  }
  return errors
}

/* ── The diff ──────────────────────────────────────────────────────────────── */

/** The thirteen answer fields, excluding the two provenance keys. */
const ANSWER_FIELDS = [
  'state',
  'state_quadrant',
  'stress_1_10',
  'sleep_hours',
  'sleep_quality',
  'woke_at_local',
  'exercised',
  'exercise_finished',
  'exercise_intensity',
  'exercise_minutes',
  'caffeine_cups',
  'caffeine_last',
  'alcohol_drinks',
  'water_glasses',
  'tags',
  'notes',
] as const

const sameTags = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((tag, i) => tag === b[i])

/**
 * The compiler's way of saying "every case is handled".
 *
 * Reached only if a `DraftField` is added without a case, which is a type error
 * at the call site rather than a runtime surprise on the Save path. It still
 * throws, because an unhandled field silently reported as "unchanged" is an
 * answer dropped without a word.
 */
function assertNever(field: never): never {
  throw new Error(`selfReport: ${String(field)} has no case`)
}

/** The stored value a touched field is compared against. */
function isChanged(
  field: DraftField,
  original: SelfReport | null,
  draft: SelfReportDraft,
): boolean {
  switch (field) {
    case 'tags':
      // Canonical on both sides, so a stored duplicate the backend accepted is
      // left alone rather than tidied, and a reordering is not an edit.
      return !sameTags(canonicalTags(original?.tags ?? []), canonicalTags(draft.tags))
    case 'notes':
      // `null` and `''` both mean "no note", so neither is a change from the
      // other. Whitespace is NOT normalised -- see the emit below.
      return (original?.notes ?? '') !== draft.notes
    case 'state': {
      /**
       * COMPARED AS A PAIR, BECAUSE IT IS ONE ANSWER IN TWO COLUMNS.
       *
       * Comparing `draft.state` against the stored `state` alone leaves an answer
       * that can be affirmed but never withdrawn. A captured journal can hold
       * `{state: null, state_quadrant: 'HVLA'}` -- a real degradation path, since
       * `from_journal` falls back cell by cell -- and `draftFromReport` then shows
       * "Calm". Press "No answer" and the scalar comparison is `null !== null`, so
       * nothing is sent: the chip deselects, Save stays disabled, and the record
       * still holds `HVLA`. The screen and the stored journal disagree, silently.
       *
       * Including the quadrant also lets a real edit repair a stored pair that
       * contradicts itself, which the backend accepts and nothing else checks.
       *
       * This is NOT the untouched-repair the earlier design was rejected for:
       * `changedFields` gates every field on `touched`, so a pair is only ever
       * rewritten by somebody who worked the control. An untouched incoherent pair
       * is still left exactly as the device wrote it.
       */
      const wantQuadrant = draft.state === null ? null : STATE_PAIRS[draft.state]
      return (
        draft.state !== (original?.state ?? null)
        || wantQuadrant !== (original?.state_quadrant ?? null)
      )
    }
    case 'stress_1_10':
      return draft.stress_1_10 !== (original?.stress_1_10 ?? null)
    case 'sleep_quality':
      return draft.sleep_quality !== (original?.sleep_quality ?? null)
    case 'woke_at_local': {
      // Read first, like the numerics: an unrepresentable draft is not a change,
      // and `draftErrors` is what blocks the save and names the field.
      const read = readClockTime(draft.woke_at_local)
      if (read.kind === 'invalid') return false
      const next = read.kind === 'empty' ? null : read.value
      return next !== (original?.woke_at_local ?? null)
    }
    case 'exercised':
      return draft.exercised !== (original?.exercised ?? null)
    case 'exercise_finished':
      return draft.exercise_finished !== (original?.exercise_finished ?? null)
    case 'caffeine_last':
      return draft.caffeine_last !== (original?.caffeine_last ?? null)
    case 'exercise_intensity':
      return draft.exercise_intensity !== (original?.exercise_intensity ?? null)
    /*
      ── NAMED, NOT `default:` ──────────────────────────────────────────────
      This branch was `default:`, and it is numeric-only: it calls
      `readNumeric(draft[field], NUMERIC_FIELDS[field])`. Every field that is
      NOT a number therefore had to be listed above it, and nothing enforced
      that — a sixteenth field added to `SelfReportDraft` would fall in here,
      look up a `NUMERIC_FIELDS` entry that does not exist, and throw inside
      `buildPatch` on the Save path. TypeScript cannot see it, because
      `default:` accepts whatever is left.

      Listing the five by name makes the switch exhaustive, so the next field
      is a compile error at the one place that has to think about it.
    */
    case 'sleep_hours':
    case 'exercise_minutes':
    case 'caffeine_cups':
    case 'alcohol_drinks':
    case 'water_glasses': {
      const read = readNumeric(draft[field], NUMERIC_FIELDS[field])
      // An unrepresentable value is not a change, so it is omitted rather than
      // sent. Defined behaviour, but unreachable through the form: `draftErrors`
      // blocks the save and names the field.
      if (read.kind === 'invalid') return false
      const next = read.kind === 'empty' ? null : read.value
      return next !== (original?.[field] ?? null)
    }
  }
  // Unreachable while every `DraftField` is cased above — and a compile error
  // the moment one is not, because `field` is then not `never`.
  return assertNever(field)
}

/**
 * Which touched fields differ from what the server last told us.
 *
 * Exported because the Save button's disabled state and the "is there anything to
 * send" decision must read the SAME computation. Two implementations of that
 * question will disagree eventually, and the way they disagree is a PATCH with an
 * empty body -- which is not a no-op: `_write_provenance`
 * (`recording/service.py:89`) runs on every patch, so it would stamp
 * `edited: true` and a fresh `recorded_at` for an edit that never happened.
 */
export function changedFields(
  original: SelfReport | null,
  draft: SelfReportDraft,
  touched: ReadonlySet<DraftField>,
): DraftField[] {
  return DRAFT_FIELDS.filter((field) => touched.has(field) && isChanged(field, original, draft))
}

export interface BuiltPatch {
  /** Every key present is a new value or an explicit `null`. Absent means preserve. */
  patch: SelfReportPatch
  changed: readonly DraftField[]
  /**
   * True when this patch would leave a report that HAD answers holding none.
   * The one destructive act the form can perform in a single pass, and there is
   * no revision history to undo it.
   */
  clearsEverything: boolean
}

/**
 * Stamp `schema_version: 2`?
 *
 * Only where it makes the typed view self-describing without contradicting
 * `raw`. `from_journal` lifts typed fields only for a version it knows
 * (`KNOWN_JOURNAL_SCHEMA_VERSIONS`), so a journal written to any other version
 * arrives with every typed field null and every answer in `raw`. Stamping over
 * that would make the record claim a schema its own verbatim copy contradicts --
 * on the single key that tells a reader how to interpret `raw`.
 *
 * ── RAISE-ONLY, AND ONLY WHEN THE EDIT EARNS IT ─────────────────────────────
 *
 * Schema 2 is a strict SUPERSET of 1: three keys added, nothing removed,
 * renamed or retyped. So a schema-1 `raw` IS a valid schema-2 document, and
 * raising a stored 1 to 2 contradicts nothing.
 *
 * But it is not free either. `schema_version` says **which questions this
 * journal was asked**, and History shows a "Schema 1" pill and dashes for Woke /
 * Finished / Last cup because of it. Relabelling an old journal v2 because
 * somebody corrected its stress score would erase that — the record would claim
 * it was asked three questions it never was.
 *
 * So the raise happens only when the patch actually writes one of schema 2's own
 * fields. Then the claim is true: this journal now holds a v2 answer. An edit
 * that touches nothing new leaves the stamp exactly where the device put it.
 *
 * This never lowers a stamp, and never stamps over a version it does not
 * recognise, because neither is a superset claim it is in a position to make.
 *
 * `raw` is read here as EVIDENCE, never as a source of values.
 */
/** The keys schema 2 added. Writing one of these is what earns the stamp. */
const SCHEMA_2_FIELDS = ['woke_at_local', 'exercise_finished', 'caffeine_last'] as const

function shouldStampSchemaVersion(
  original: SelfReport | null,
  changed: readonly DraftField[],
): boolean {
  if (original === null) return true
  // The typed field first, then `raw`'s: `from_journal` leaves the typed one
  // null for a version it cannot read, and that is exactly the case where `raw`
  // is the only witness.
  const rawVersion = (original.raw as Record<string, unknown> | null | undefined)?.schema_version
  const claimed = original.schema_version ?? rawVersion ?? null
  if (claimed === null || claimed === undefined) return true
  if (claimed === JOURNAL_SCHEMA_VERSION) return false
  if (claimed !== 1) return false
  return changed.some((field) => (SCHEMA_2_FIELDS as readonly string[]).includes(field))
}

const isAnswered = (value: unknown): boolean =>
  Array.isArray(value) ? value.length > 0 : value !== null && value !== undefined

export function buildPatch(
  original: SelfReport | null,
  draft: SelfReportDraft,
  touched: ReadonlySet<DraftField>,
  options: { now?: string } = {},
): BuiltPatch {
  const changed = changedFields(original, draft, touched)
  const patch: SelfReportPatch = {}

  for (const field of changed) {
    switch (field) {
      case 'state':
        // ── THE PAIR, TOGETHER OR NOT AT ALL ───────────────────────────────
        // One answer, two wire fields. The quadrant is never written from
        // anything but `STATE_PAIRS`, and clearing sends both as null, so a
        // half-written or self-contradicting pair cannot originate here.
        patch.state = draft.state
        patch.state_quadrant = draft.state === null ? null : STATE_PAIRS[draft.state]
        break
      case 'tags':
        // Never `null`. Reaching an empty set requires having WORKED the
        // control, which is an answer -- "I checked and none applied" -- and
        // `null` would downgrade it to "nobody asked".
        patch.tags = canonicalTags(draft.tags)
        break
      case 'notes':
        // Sent exactly as typed. Trimming would rewrite the one free-text field
        // a person wrote in their own words, and criterion 2 permits the client
        // to alter a submitted value only for a documented enum mapping.
        patch.notes = draft.notes === '' ? null : draft.notes
        break
      case 'stress_1_10':
        patch.stress_1_10 = draft.stress_1_10
        break
      case 'sleep_quality':
        patch.sleep_quality = draft.sleep_quality
        break
      case 'woke_at_local': {
        const read = readClockTime(draft.woke_at_local)
        patch.woke_at_local = read.kind === 'value' ? read.value : null
        break
      }
      case 'exercised':
        patch.exercised = draft.exercised
        break
      case 'exercise_finished':
        patch.exercise_finished = draft.exercise_finished
        break
      case 'caffeine_last':
        patch.caffeine_last = draft.caffeine_last
        break
      case 'exercise_intensity':
        patch.exercise_intensity = draft.exercise_intensity
        break
      // Named for the reason `isChanged`'s are: this branch is numeric-only.
      case 'sleep_hours':
      case 'exercise_minutes':
      case 'caffeine_cups':
      case 'alcohol_drinks':
      case 'water_glasses': {
        const read = readNumeric(draft[field], NUMERIC_FIELDS[field])
        patch[field] = read.kind === 'value' ? read.value : null
        break
      }
    }
  }

  // ── PROVENANCE RIDES ALONG AND NEVER TRAVELS ALONE ──────────────────────
  // Gated on a non-empty substantive diff, so neither key can be the sole
  // reason a request is sent -- which would be an edit nobody made.
  if (changed.length > 0) {
    if (shouldStampSchemaVersion(original, changed)) patch.schema_version = JOURNAL_SCHEMA_VERSION
    // Only ever FILLS a blank, on a genuinely new report. Stamping "now" onto a
    // journal written three days ago would assert the person answered today, and
    // the server already owns "when was this last written" as
    // `self_report_provenance.recorded_at`.
    if (original === null) patch.answered_at = options.now ?? new Date().toISOString()
  }

  const had = ANSWER_FIELDS.some((field) => isAnswered(original?.[field]))
  const leaves = ANSWER_FIELDS.some((field) =>
    isAnswered(field in patch ? patch[field] : original?.[field]),
  )

  return { patch, changed, clearsEverything: had && !leaves }
}

/* ── The last guard, and what the device also wrote ────────────────────────── */

/** Every key `SelfReportPatch` permits. `raw` is deliberately not among them. */
const PATCHABLE = new Set<string>([...ANSWER_FIELDS, 'schema_version', 'answered_at'])

/**
 * Throw rather than send something the contract will refuse.
 *
 * A belt, not the braces: the shapes above are what make these unreachable. It
 * exists because the alternative to an exception here is a 400 the user reads as
 * a server fault, and because `raw` must be impossible rather than merely absent
 * -- sending one is `400 request.invalid` naming the field.
 *
 * **Not the 422 two backend docstrings claim**: `api/routes/recordings.py:247` and
 * `contracts/recording.py:393`, the second of which is inlined into the generated
 * `api.types.ts` and so reads as authoritative from in here. The sprint spec itself
 * says only "reject an attempted `raw` write" and names no code, so it is not one
 * of the claimants -- an earlier version of this comment said it was.
 */
export function assertWireSafe(patch: SelfReportPatch): void {
  for (const [key, value] of Object.entries(patch)) {
    if (key === 'raw') throw new Error('selfReport: raw is never the client\'s to write')
    if (!PATCHABLE.has(key)) throw new Error(`selfReport: ${key} is not a patchable field`)
    if (value === null) continue

    if (key === 'tags') {
      if (!Array.isArray(value)) throw new Error('selfReport: tags must be an array')
      for (const tag of value) {
        if (!(JOURNAL_TAGS as readonly string[]).includes(tag)) {
          throw new Error(`selfReport: ${String(tag)} is not one of the eight tags`)
        }
      }
      continue
    }
    if (key === 'notes' || key === 'answered_at' || key === 'state' || key === 'state_quadrant'
      || key === 'sleep_quality' || key === 'exercise_intensity'
      // Schema 2's three. Listed here rather than left to the numeric branch
      // below, which would reject every one of them as "must be a number" --
      // the generic fallthrough is the trap this function exists to catch, and
      // adding a string field without adding it here turns a contract error
      // into a thrown exception on the save path.
      || key === 'woke_at_local' || key === 'exercise_finished' || key === 'caffeine_last') {
      if (typeof value !== 'string') throw new Error(`selfReport: ${key} must be a string`)
      continue
    }
    if (key === 'exercised') {
      if (typeof value !== 'boolean') throw new Error('selfReport: exercised must be a boolean')
      continue
    }
    // Everything left is numeric. A string here is trap 1 -- `input.value` is
    // always a string, and the contract's fields are `strict=True`, so `"8"` is a
    // 400 rather than 8.
    if (typeof value !== 'number') throw new Error(`selfReport: ${key} must be a number, got ${typeof value}`)
    if (!Number.isFinite(value)) throw new Error(`selfReport: ${key} must be finite`)
    if ((key === 'stress_1_10' || key === 'schema_version') && !Number.isInteger(value)) {
      throw new Error(`selfReport: ${key} must be an integer`)
    }
  }
}

/**
 * The device's verbatim keys that the typed view is not already showing.
 *
 * ── WHY THE FILTER IS "TYPED FIELD IS NULL" AND NOT "UNKNOWN KEY" ───────────
 *
 * `raw` is the WHOLE journal, so it routinely repeats the typed fields. Filtering
 * on "keys that are not typed field names" would hide a duplicate correctly and
 * then hide the case that matters: `from_journal` lifts nothing at all unless
 * `schema_version` is exactly the integer 1, so a journal written to any other
 * version arrives with fifteen nulls and every answer sitting in `raw`. That form
 * would open completely BLANK for a person who filled it in on the device, and
 * this block -- the only evidence their journal survived -- would be empty too.
 *
 * One rule covers both: show a raw key while the typed field it corresponds to
 * holds nothing. Answer that field and it drops out, because the new answer
 * supersedes it.
 */
export function untypedRawEntries(report: SelfReport | null): Array<[string, unknown]> {
  const raw = report?.raw as Record<string, unknown> | null | undefined
  if (!raw) return []
  return Object.entries(raw).filter(([key, value]) => {
    if (key === 'raw' || value === null || value === undefined) return false
    // An empty list or object says nothing a reader could use, so a key holding one
    // is not "also recorded" -- it would advertise a name with nothing beside it.
    // One check, not two: an array IS an object, so `Object.keys([])` is also empty.
    if (typeof value === 'object' && Object.keys(value as object).length === 0) return false
    if (!PATCHABLE.has(key)) return true
    /**
     * "The typed view holds NO VALUE", not "holds no answer".
     *
     * `isAnswered` is the right question for `clearsEverything`, where an empty tag
     * list is not an answer being preserved. It is the wrong one here: `tags: []`
     * is a present typed value, and treating it as absent makes the typed field and
     * its raw twin both render -- the duplicate this filter exists to prevent.
     */
    const typed = (report as Record<string, unknown>)[key]
    return typed === null || typed === undefined
  })
}

/**
 * How to describe where a journal came from, without saying more than the server did.
 *
 * `edited: null` means UNKNOWN, not "no" -- every row written before Sprint 1.9
 * is in that state, and rendering an absence as a negative would be a fabricated
 * claim on the very field a person is shown in order to trust the reading.
 */
export function provenanceWords(
  provenance: SelfReportProvenance | null | undefined,
): { origin: string; edited: string | null } | null {
  if (!provenance) return null
  const origin = {
    captured: 'Captured with your recording',
    experiment_report: 'Experiment report',
    manual: 'You added this here',
    unknown: 'Source not recorded',
  }[provenance.origin]
  const edited =
    provenance.edited === true ? 'Edited' : provenance.edited === false ? null : 'Edits not recorded'
  return { origin: origin ?? 'Source not recorded', edited }
}
